import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Tooltip } from '../../components';
import MentorAvailabilityModal from '../../components/MentorAvailabilityModal';
import MentorshipRequests from '../mentor/MentorshipRequests';
import { getCurrentUser, setCurrentUser } from '../../data/session';
import * as sessionService from '../../services/group-session-service';
import { setSessionMeetLink } from '../../services/group-session-service';
import * as pitchService from '../../services/pitch-service';
import { listFinalPitchesForReview } from '../../services/pitch-submission-service';
import { getMe } from '../../services/user-service';

interface SessionDisplay {
  id: string;
  title: string;
  scheduledAt: string;
  duration: number;
  meetLink: string;
  status: string;
  mentorName: string;
  agenda: string;
  attendees: any[];
  participants: any[];
}

interface ReviewDisplay {
  id: string;
  startupName: string;
  title: string;
  status: string;
  submittedAt: string;
  type: 'idea' | 'pitch' | 'final-pitch';
}

const statusReviewBadge: Record<string, { label: string; color: string; tooltip: string }> = {
  submitted:            { label: 'Pending',                 color: 'bg-chart/10 text-chart',     tooltip: 'Awaiting your review' },
  pending:              { label: 'Pending',                 color: 'bg-chart/10 text-chart',     tooltip: 'Awaiting your review' },
  approved:             { label: 'Approved',                color: 'bg-success/10 text-success', tooltip: 'You have approved this submission' },
  approved_distinction: { label: 'Approved ★ Distinction', color: 'bg-chart/10 text-chart',     tooltip: 'Approved with distinction' },
  revision_needed:      { label: 'Revision Needed',         color: 'bg-warning/10 text-warning', tooltip: 'You requested revisions' },
  rejected:             { label: 'Rejected',                color: 'bg-danger/10 text-danger',   tooltip: 'This submission was rejected' },
};

const isPast = (s: SessionDisplay) => {
  const end = new Date(s.scheduledAt).getTime() + s.duration * 60 * 1000;
  return end < Date.now();
};

const CalendarIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="w-4 h-4 text-muted flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

/* ── Detail Modal ── */
const SessionDetailModal: React.FC<{ session: SessionDisplay; onClose: () => void }> = ({ session, onClose }) => {
  const past = isPast(session);
  const cancelled = session.status === 'cancelled';

  const mergedParticipants = session.participants?.length
    ? session.participants.map((p: any) => {
        const att = session.attendees?.find((a: any) => String(a.userId) === String(p.userId));
        return { name: p.name || 'Unknown', status: att?.status ?? 'invited' };
      })
    : session.attendees?.map((a: any) => ({ name: a.name || 'Unknown', status: a.status })) ?? [];

  const statusColor: Record<string, string> = {
    present:   'bg-success/15 text-success',
    absent:    'bg-danger/15 text-danger',
    confirmed: 'bg-chart/15 text-chart',
    invited:   'bg-muted/15 text-muted',
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <Modal isOpen onClose={onClose} title={session.title} className="max-w-lg">
      <div className="space-y-5">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted">
            <CalendarIcon />
            <span className="text-text">{fmtDate(session.scheduledAt)} at {fmtTime(session.scheduledAt)}</span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <ClockIcon />
            <span className="text-text">{session.duration} minutes</span>
          </div>
        </div>

        {session.agenda && (
          <div>
            <p className="text-xs text-muted uppercase tracking-wider font-semibold mb-1.5">Agenda</p>
            <p className="text-sm text-text whitespace-pre-wrap">{session.agenda}</p>
          </div>
        )}

        {mergedParticipants.length > 0 && (
          <div>
            <p className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">
              Participants ({mergedParticipants.length})
            </p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {mergedParticipants.map((p: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-text">{p.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[p.status] ?? 'bg-muted/15 text-muted'}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!past && !cancelled && session.meetLink && (
          <a
            href={session.meetLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-chart text-white text-sm font-medium rounded-lg hover:bg-chart/90 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
            </svg>
            Join Session
          </a>
        )}
        {!past && !cancelled && !session.meetLink && (
          <p className="text-xs text-muted text-center">Meeting link will be shared soon.</p>
        )}
      </div>
    </Modal>
  );
};

/* ── Dashboard ── */
const MentorDashboard = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const firstName = user.name?.split(' ')[0] || 'Mentor';

  const [sessions, setSessions]       = useState<SessionDisplay[]>([]);
  const [reviews, setReviews]         = useState<ReviewDisplay[]>([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [detailSession, setDetail]    = useState<SessionDisplay | null>(null);
  const [showAvailModal, setShowAvailModal] = useState(false);
  const [addLinkSession, setAddLinkSession] = useState<SessionDisplay | null>(null);
  const [linkInput, setLinkInput]     = useState('');
  const [savingLink, setSavingLink]   = useState(false);

  const handleSaveLink = async () => {
    if (!addLinkSession || !linkInput.trim()) return;
    setSavingLink(true);
    try {
      await setSessionMeetLink(addLinkSession.id, linkInput.trim());
      setSessions(prev => prev.map(s => s.id === addLinkSession.id ? { ...s, meetLink: linkInput.trim() } : s));
      setAddLinkSession(null);
      setLinkInput('');
    } catch {}
    setSavingLink(false);
  };

  const handleAvailabilitySaved = useCallback(async () => {
    try {
      const fresh = await getMe();
      setCurrentUser(fresh);
    } catch {}
    setShowAvailModal(false);
  }, []);

  useEffect(() => {
    Promise.all([
      getMe(),
      sessionService.getGroupSessions(),
      pitchService.getAllIdeasForReview(),
      pitchService.getAllPitchesForReview(),
      listFinalPitchesForReview(),
    ])
      .then(([profile, sessionData, ideas, pitches, finalPitches]) => {
        setCurrentUser(profile);
        setShowAvailModal(!(profile as any)?.mentor?.availabilitySet);
        setSessions(sessionData.map((s: any) => ({
          id: s._id,
          title: s.title || 'Group Session',
          scheduledAt: s.scheduledAt,
          duration: s.duration || 60,
          meetLink: s.meetLink || '',
          status: s.status,
          mentorName: s.mentorId?.name || '',
          agenda: s.agenda || '',
          attendees: s.attendees || [],
          participants: s.participants || [],
        })));
        setReviews([
          ...ideas.map((i: any) => ({
            id: i._id,
            startupName: i.startupId?.name || 'Unknown',
            title: i.title || 'Idea Submission',
            status: i.status,
            submittedAt: i.submittedAt || i.createdAt,
            type: 'idea' as const,
          })),
          ...pitches.map((p: any) => ({
            id: p._id,
            startupName: p.startupId?.name || 'Unknown',
            title: p.problem?.slice(0, 55) || 'Pitch Submission',
            status: p.status,
            submittedAt: p.submittedAt || p.createdAt,
            type: 'pitch' as const,
          })),
          ...finalPitches.map((fp: any) => ({
            id: fp._id,
            startupName: fp.userId?.name || 'Unknown',
            title: fp.problem?.slice(0, 55) || 'Final Pitch',
            status: fp.reviewStatus || 'pending',
            submittedAt: fp.submittedAt || fp.createdAt,
            type: 'final-pitch' as const,
          })),
        ]);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const fmtShortDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const upcoming = sessions.filter(s => !isPast(s) && s.status !== 'cancelled');
  const previous = sessions.filter(s => isPast(s) || s.status === 'cancelled' || s.status === 'completed');
  const pendingCount = reviews.filter(r => r.status === 'submitted').length;
  const totalParticipants = sessions.reduce((sum, s) => sum + (s.participants?.length || s.attendees?.length || 0), 0);

  const SectionLabel = ({ label, count }: { label: string; count: number }) => (
    <div className="flex items-center gap-2.5 mb-4">
      <h2 className="font-display text-2xl tracking-wide text-text uppercase">{label}</h2>
      <span className="text-xs bg-surface border border-line text-muted px-2 py-0.5 rounded-full font-medium">{count}</span>
    </div>
  );

  return (
    <div className="space-y-10 pb-10">
      {showAvailModal && <MentorAvailabilityModal onSaved={handleAvailabilitySaved} />}

      {addLinkSession && (
        <Modal isOpen onClose={() => { setAddLinkSession(null); setLinkInput(''); }} title="Add Meeting Link">
          <div className="space-y-4">
            <p className="text-sm text-muted">{addLinkSession.title}</p>
            <div>
              <label className="block text-sm font-medium text-text mb-1.5">Meeting URL</label>
              <input
                type="url"
                value={linkInput}
                onChange={e => setLinkInput(e.target.value)}
                placeholder="https://meet.google.com/… or https://zoom.us/…"
                className="w-full bg-surface border border-line rounded-lg px-3.5 py-2.5 text-sm text-text placeholder:text-muted focus:outline-none focus:border-chart/50"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <Button variant="ghost" onClick={() => { setAddLinkSession(null); setLinkInput(''); }} disabled={savingLink}>Cancel</Button>
              <Button variant="primary" onClick={handleSaveLink} disabled={savingLink || !linkInput.trim()}>
                {savingLink ? 'Saving…' : 'Save & Notify Participants'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-2">
        <div>
          <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Mentor Dashboard</p>
          <h1 className="font-display text-5xl tracking-wide text-text leading-none">
            Hello, {firstName}.
          </h1>
          <p className="font-serif-display italic text-text text-xl mt-2">
            Here's everything on your plate today.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:pb-1">
          <Tooltip content="Sessions you are scheduled to lead">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-chart">{isLoading ? '–' : upcoming.length}</span>
              <span className="text-muted">Upcoming</span>
            </span>
          </Tooltip>
          <Tooltip content="Idea and pitch submissions awaiting your feedback">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-warning">{isLoading ? '–' : pendingCount}</span>
              <span className="text-muted">Pending Reviews</span>
            </span>
          </Tooltip>
          <Tooltip content="Founders across all your sessions">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-success">{isLoading ? '–' : totalParticipants}</span>
              <span className="text-muted">Participants</span>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── Upcoming Sessions ── */}
      <section>
        <SectionLabel label="Upcoming Sessions" count={upcoming.length} />

        {isLoading ? (
          <p className="text-muted text-sm font-serif-display italic">Loading sessions…</p>
        ) : upcoming.length === 0 ? (
          <p className="font-serif-display italic text-muted text-base">No sessions scheduled — enjoy the calm.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {upcoming.map((s, i) => (
              <div
                key={s.id}
                className="bg-shell border border-line rounded-card p-5 flex flex-col gap-4 animate-fade-in-up"
                style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'both' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-text text-sm leading-snug flex-1">{s.title}</h3>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!s.meetLink && (
                      <Tooltip content="Add meeting link — participants will be notified by email" position="left">
                        <button
                          onClick={() => { setAddLinkSession(s); setLinkInput(''); }}
                          className="flex items-center gap-1 text-xs bg-warning/10 text-warning px-2 py-0.5 rounded-full hover:bg-warning/20 transition-colors"
                        >
                          + Add Link
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip content="Registered participants" position="left">
                      <span className="flex items-center gap-1 text-xs bg-chart/10 text-chart px-2 py-0.5 rounded-full cursor-default">
                        <UsersIcon />
                        {s.participants?.length || s.attendees?.length || 0}
                      </span>
                    </Tooltip>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs text-muted">
                    <CalendarIcon />
                    <span>{fmtDate(s.scheduledAt)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted">
                    <ClockIcon />
                    <span>{fmtTime(s.scheduledAt)} · {s.duration} min</span>
                  </div>
                </div>

                {s.agenda && (
                  <p className="text-xs text-muted line-clamp-2 border-t border-line pt-3">{s.agenda}</p>
                )}

                <div className="flex gap-2 mt-auto pt-1">
                  {s.meetLink ? (
                    <a href={s.meetLink} target="_blank" rel="noopener noreferrer">
                      <Button variant="primary" size="sm">Join Session</Button>
                    </a>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => { setAddLinkSession(s); setLinkInput(''); }}>
                      Add Link
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setDetail(s)}>
                    Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Bottom two columns ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* ── Pitch Reviews ── */}
        <section>
          <SectionLabel label="Pitch Reviews" count={reviews.length} />

          {isLoading ? (
            <p className="text-muted text-sm font-serif-display italic">Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <p className="font-serif-display italic text-muted text-base">No submissions to review right now.</p>
          ) : (
            <div className="bg-shell border border-line rounded-card divide-y divide-line overflow-hidden max-h-[520px] overflow-y-auto">
              {reviews.map((r, i) => {
                const badge = statusReviewBadge[r.status] || statusReviewBadge.submitted;
                return (
                  <button
                    key={r.id}
                    onClick={() => r.type === 'final-pitch'
                      ? navigate(`/app/pitch/final-review/${r.id}`)
                      : navigate(`/app/pitch/review/${r.id}?type=${r.type}`)
                    }
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-surface transition-colors text-left animate-fade-in-up"
                    style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'both' }}
                  >
                    <div className="flex-1 min-w-0 pr-3">
                      <p className="font-display text-micro tracking-widest text-chart uppercase mb-0.5">
                        {r.startupName} · {r.type === 'final-pitch' ? 'Final Pitch' : r.type}
                      </p>
                      <p className="text-sm font-medium text-text leading-snug line-clamp-1">{r.title}</p>
                      <p className="text-xs text-muted mt-0.5 tabular-nums">{fmtShortDate(r.submittedAt)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Tooltip content={badge.tooltip} position="left">
                        <span className={`text-badge font-medium px-2 py-0.5 rounded-full cursor-default ${badge.color}`}>
                          {badge.label}
                        </span>
                      </Tooltip>
                      <ChevronIcon />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Previous Sessions ── */}
        <section>
          <SectionLabel label="Previous Sessions" count={previous.length} />

          {isLoading ? (
            <p className="text-muted text-sm font-serif-display italic">Loading…</p>
          ) : previous.length === 0 ? (
            <p className="font-serif-display italic text-muted text-base">No completed sessions yet.</p>
          ) : (
            <div className="bg-shell border border-line rounded-card divide-y divide-line overflow-hidden max-h-[520px] overflow-y-auto">
              {previous.map(s => (
                <button
                  key={s.id}
                  onClick={() => setDetail(s)}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-surface transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-text truncate block">{s.title}</span>
                    <span className="text-xs text-muted tabular-nums">
                      {fmtDate(s.scheduledAt)} · {s.duration} min · {s.participants?.length || s.attendees?.length || 0} participant{(s.participants?.length || s.attendees?.length || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <span className="text-badge bg-success/10 text-success px-2 py-0.5 rounded-full">
                      Completed
                    </span>
                    <ChevronIcon />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

      </div>

      {/* ── Mentorship Requests ── */}
      <section>
        <MentorshipRequests />
      </section>

      {detailSession && (
        <SessionDetailModal session={detailSession} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

export default MentorDashboard;
